import { TextureSource } from "@deck.gl/core"
import { BitmapLayer } from "@deck.gl/layers"
import { parse } from "@loaders.gl/core"
import { OrthoPalettes, SupportedRegions } from "@nova/constants"
import { Coordinate } from "@nova/protobuf"
import { convertBboxToExtent } from "@nova/calculate-geospatial"
import { getGlobalTerrainData } from "features/map/GlobalTerrainManager"
import { TrackedTileLayer } from "features/map/layers/cog/TrackedTileLayer"
import { LRUCache } from "lru-cache"
import { ComparisonClip, OrthoComparisonExtension } from "./OrthoComparisonExtension"
import { OrthoMaskExtension, toMaskPolygon } from "./OrthoMaskExtension"

type TileManifest = { tiles: Record<string, Record<string, string[]>> }

const createFileLoader = (root: string) => {
    const cache = new LRUCache<string, object, unknown>({
        max: 20,
        fetchMethod: async (key) => {
            const [format, filename] = JSON.parse(key) as [format: "binary" | "json", string]
            const response = await fetch(`${root}/${filename}`, {
                headers: {
                    region:
                        JSON.parse(sessionStorage.getItem("region") || "null") ||
                        SupportedRegions.Canada,
                },
            })
            return format === "binary" ? await response.blob() : await response.json()
        },
    })

    const load = (format: "binary" | "json", filename: string) =>
        cache.fetch(JSON.stringify([format, filename]))

    return {
        binary: (filename: string) => load("binary", filename) as Promise<Blob>,
        json: <T>(filename: string) => load("json", filename) as Promise<T>,
    }
}

const legacyTile = async (
    loader: ReturnType<typeof createFileLoader>,
    { x, y, z }: { x: number; y: number; z: number },
    palette: OrthoPalettes | undefined,
    signal: AbortSignal | undefined
) => {
    const { tiles } = await loader.json<TileManifest>("orthophoto_tiles/tiles.json")
    if (signal?.aborted) return null

    const tmsY = (1 << z) - y - 1
    if (!tiles?.[z]?.[x]?.includes(`${tmsY}.png`)) return null

    const blob = await loader.binary(
        `orthophoto_tiles/${z}/${x}/${y}.png?tile=true${palette ? `&palette=${palette}` : ""}`
    )
    if (signal?.aborted) return null

    return (await parse(blob.slice(0, blob.size, "image/png"))) as ImageData
}

export type LegacyOrthoLayerOptions = {
    id: string
    fileroot: string
    bbox?: Coordinate[]
    minZoom?: number
    maxZoom?: number
    opacity?: number
    palette?: OrthoPalettes
    comparisonClip?: ComparisonClip
    maskPolygon?: Coordinate[] | null
    transform?: (data: ImageData) => ImageData
}

export const LegacyOrthoLayer = ({
    id,
    fileroot,
    bbox,
    minZoom,
    maxZoom,
    opacity,
    palette,
    comparisonClip,
    maskPolygon,
    transform,
}: LegacyOrthoLayerOptions) => {
    const loader = createFileLoader(fileroot)
    const activeMask = toMaskPolygon(maskPolygon)

    return new TrackedTileLayer({
        id,
        minZoom,
        maxZoom,
        tileSize: 256,
        zRange: [0, 2000],
        maxRequests: 6,
        extent: bbox?.length ? convertBboxToExtent(bbox) : undefined,
        refinementStrategy: opacity === 100 ? "best-available" : "no-overlap",
        updateTriggers: {
            getTileData: [fileroot, palette],
            renderSubLayers: [
                opacity,
                palette,
                comparisonClip?.threshold,
                comparisonClip?.axis,
                comparisonClip?.hideHigh,
                maskPolygon,
                transform,
            ],
        },
        getTileData: ({ index, signal }) => legacyTile(loader, index, palette, signal),
        renderSubLayers: (props) => {
            const { bbox: tileBbox } = props.tile
            if (!("west" in tileBbox)) throw new Error("LegacyOrthoLayer: expected a geo bbox")

            const raw = (props.tile.data ?? null) as ImageData | null
            const data = raw && transform ? transform(raw) : raw

            return new BitmapLayer({
                id: `${props.id}-bitmap`,
                image: data as TextureSource,
                bounds: [tileBbox.west, tileBbox.south, tileBbox.east, tileBbox.north],
                extensions: [
                    ...getGlobalTerrainData().extensions,
                    ...(comparisonClip ? [new OrthoComparisonExtension()] : []),
                    ...(activeMask ? [new OrthoMaskExtension()] : []),
                ],
                opacity,
                comparisonClip: comparisonClip ?? null,
                maskPolygon: activeMask,
            })
        },
    })
}
